import { Injectable } from '@angular/core';

export type OtpPurpose = 'ShipmentTracking' | 'ComplianceDocuments';

export interface OtpSessionExtras {
  sellerName?: string;
  shipmentAmount?: number;
  shipmentAmountCurrency?: string;
}

export interface OtpSessionEntry {
  referenceId: string;
  purpose: OtpPurpose;
  token: string;
  tokenExpiresAtUtc: string;
  maskedPhone?: string;
  extras?: OtpSessionExtras;
}

const STORAGE_PREFIX = 'otp_session_';
const CLOCK_SKEW_SECONDS = 5;

/**
 * Single source of truth for OTP session tokens.
 * Replaces the legacy ShipmentOtpStorageService / ComplianceOtpStorageService
 * which stored the raw OTP code and replayed it on every protected call.
 */
@Injectable({ providedIn: 'root' })
export class OtpSessionService {
  save(entry: OtpSessionEntry): void {
    if (!entry.referenceId || !entry.token) return;
    try {
      sessionStorage.setItem(this.key(entry.referenceId, entry.purpose), JSON.stringify(entry));
    } catch {
      // sessionStorage unavailable
    }
  }

  get(referenceId: string, purpose: OtpPurpose): OtpSessionEntry | null {
    if (!referenceId) return null;
    try {
      const raw = sessionStorage.getItem(this.key(referenceId, purpose));
      if (!raw) return null;
      return JSON.parse(raw) as OtpSessionEntry;
    } catch {
      return null;
    }
  }

  isValid(referenceId: string, purpose: OtpPurpose): boolean {
    const entry = this.get(referenceId, purpose);
    if (!entry?.token || !entry.tokenExpiresAtUtc) return false;
    const expiresAt = new Date(entry.tokenExpiresAtUtc).getTime();
    if (!Number.isFinite(expiresAt)) return false;
    return expiresAt - CLOCK_SKEW_SECONDS * 1000 > Date.now();
  }

  clear(referenceId: string, purpose: OtpPurpose): void {
    if (!referenceId) return;
    try {
      sessionStorage.removeItem(this.key(referenceId, purpose));
    } catch {
      // sessionStorage unavailable
    }
  }

  private key(referenceId: string, purpose: OtpPurpose): string {
    return `${STORAGE_PREFIX}${purpose}_${referenceId}`;
  }
}
